"use client";

import { createRestDataProviderClient } from "@providers/data-provider/rest-data-provider.client";
import { isFilterValueExist, parseFilter, transformQueryParamsWithoutLocale } from "@shared/utils";
import {
  Button,
  Checkbox,
  ComboboxOption,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  SearchInput,
} from "@ubud-ui/react-ui-kit";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useRouter, useSearchParams } from "next/navigation";
import qs from "qs";
import { useEffect, useMemo, useState } from "react";

type Skill = {
  id: string;
  key: string;
  name: string;
};

const FILTER_KEY = "skills";

function SkillFilterDialog({ path }: { path: string }) {
  type FilterItem = {
    id: string;
    label: string;
  };

  const [isOpen, setIsOpen] = useState(false);

  const router = useRouter();
  const searchParams = useSearchParams();

  const restDataProviderClient = createRestDataProviderClient();

  const [keyword, setKeyword] = useState<string>("");

  const query = useInfiniteQuery({
    queryKey: ["vacancy-skill-filter-dialog", keyword],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await restDataProviderClient.custom({
        url: transformQueryParamsWithoutLocale("/karirhub/v2/skills", {
          keyword: keyword || undefined,
          limit: 100,
          page: pageParam,
        }),
        method: "GET",
      });

      return response;
    },
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.meta?.pagination?.currentPage;
      const lastPageNumber = lastPage.meta?.pagination?.lastPage;

      if (currentPage < lastPageNumber) {
        return currentPage + 1;
      }

      return undefined;
    },
    enabled: isOpen,
    initialPageParam: 1,
  });

  const options = useMemo(() => {
    const data =
      query.data?.pages.flatMap((page) =>
        (page.data || []).map((it: Skill) => ({
          ...it,
        })),
      ) ?? [];

    return data.map((item) => ({
      value: `${item.key}`,
      label: item.name,
    }));
  }, [query.data]);

  const groupedOptions = useMemo(() => {
    return [...options]
      .sort((a, b) => a.label.localeCompare(b.label, "id"))
      .reduce(
        (acc, option) => {
          const letter = option.label.charAt(0).toUpperCase();

          if (!acc[letter]) {
            acc[letter] = [];
          }

          acc[letter].push(option);

          return acc;
        },
        {} as Record<string, ComboboxOption[]>,
      );
  }, [options]);

  const handleLoadMore = () => {
    if (query.hasNextPage) {
      query.fetchNextPage();
    }
  };

  const handleSearch = (keyword: string) => {
    setKeyword(keyword);
  };

  const [checkedItems, setCheckedItems] = useState<FilterItem[]>([]);

  useEffect(() => {
    if (!isOpen) return;

    const params = qs.parse(searchParams.toString());

    let data = params[FILTER_KEY];

    if (!data) {
      setCheckedItems([]);
      return;
    }

    if (!Array.isArray(data)) {
      data = [data];
    }

    setCheckedItems(data as FilterItem[]);
  }, [isOpen, searchParams]);

  const isChecked = (value: string) => {
    return checkedItems.some((item) => item.id === value);
  };

  const handleFilter = (option: ComboboxOption, checked: boolean) => {
    const id = option.value;

    const item = {
      id,
      label: option.label,
    };

    if (checked) {
      setCheckedItems((prev) => {
        const exists = prev.some((it) => it.id === item.id);

        if (exists) return prev;

        return [...prev, item];
      });
    } else {
      setCheckedItems((prev) => prev.filter((it) => it.id !== item.id));
    }
  };

  const handleSubmitFilter = () => {
    const params = qs.parse(searchParams.toString());

    const nextParams = {
      ...params,
      [FILTER_KEY]: checkedItems.length > 0 ? checkedItems : undefined,
      page: undefined,
    };

    router.push(
      `${path}?${qs.stringify(nextParams, {
        skipNulls: true,
      })}`,
      {
        scroll: false,
      },
    );

    setIsOpen(false);
  };

  const handleCancel = () => {
    const params = qs.parse(searchParams.toString());

    let data = params[FILTER_KEY] as FilterItem[];

    if (!data) {
      setCheckedItems([]);
    } else {
      setCheckedItems(Array.isArray(data) ? data : [data]);
    }

    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
      <DialogTrigger asChild>
        <Button variant="link" className="text-sm font-medium text-primary hover:underline m-0 p-0" type="button">
          Lihat Lebih Banyak
        </Button>
      </DialogTrigger>
      <DialogContent hideCloseButton>
        <DialogHeader className="gap-2">
          <div className="flex items-center gap-4 justify-between">
            <DialogTitle>Filter Keahlian</DialogTitle>
            <span className="text-gray-500 text-sm">Menampilkan {options.length} keahlian</span>
          </div>
          <div>
            <SearchInput placeholder="Cari keahlian" value={keyword} onSearch={handleSearch} />
          </div>
        </DialogHeader>

        <DialogBody>
          <div className="-mx-6">
            {options && options.length > 0 ? (
              <>
                <div className="space-y-5 pt-5 pb-2">
                  {Object.entries(groupedOptions).map(([letter, options]) => (
                    <div key={letter}>
                      <div className="sticky top-0 bg-white py-2 px-6 text-sm font-semibold text-gray-500 border-b">{letter}</div>

                      <div className="space-y-1 mt-3 px-3">
                        {options.map((option, index) => (
                          <div
                            className="flex items-start gap-3 hover:bg-gray-100 cursor-pointer rounded-lg"
                            key={`skill-filter-dialog-${option.value}-${index}`}
                          >
                            <Checkbox
                              className="ml-3 my-3"
                              id={`skill-filter-dialog-${option.value}-${index}`}
                              onCheckedChange={(checked) => handleFilter(option, checked as boolean)}
                              checked={isChecked(option.value)}
                            />

                            <label htmlFor={`skill-filter-dialog-${option.value}-${index}`} className="block w-full cursor-pointer py-2">
                              {option.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {query.hasNextPage ? (
                  <div className="px-6">
                    <Button
                      variant="link"
                      className="text-sm font-medium text-primary hover:underline m-0 p-0"
                      type="button"
                      onClick={handleLoadMore}
                      disabled={query.isFetchingNextPage}
                    >
                      {query.isFetchingNextPage ? "Sedang memuat..." : "Tampilkan lebih banyak"}
                    </Button>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="text-center font-bold pt-5 pb-2 min-h-[500px]">
                {!keyword ? "Ketik untuk mencari keahlian" : "Keahlian tidak ditemukan"}
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <div>
            <Button variant="outline" className="font-semibold w-full" onClick={handleCancel}>
              Batal
            </Button>
          </div>
          <div>
            <Button className="font-semibold w-full" onClick={handleSubmitFilter}>
              Terapkan
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SkillFilter({ path }: { path: string }) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const restDataProviderClient = createRestDataProviderClient();

  const [keyword, setKeyword] = useState<string>("");
  const { data: optionResult } = useQuery<{ data: Skill[] }>({
    queryKey: ["vacancy-skill-filter-", keyword],
    queryFn: () =>
      restDataProviderClient.custom({
        url: transformQueryParamsWithoutLocale(
          "/karirhub/v2/skills",
          keyword
            ? {
                keyword: keyword,
                limit: 5,
              }
            : {
                limit: 5,
              },
        ),
        method: "GET",
      }),
  });
  const options: ComboboxOption[] = optionResult?.data
    ? optionResult?.data?.map((it) => {
        return {
          value: it.key,
          label: it.name,
        };
      })
    : [];

  const handleSearch = (keyword: string) => {
    setKeyword(keyword);
  };

  const handleFilter = (option: ComboboxOption, checked: boolean) => {
    const paramsString = searchParams.toString();

    const value = {
      id: option.value,
      label: option.label,
    };

    const params = parseFilter(FILTER_KEY, value, qs.parse(paramsString), checked);

    router.push(`${path}?${qs.stringify(params, { skipNulls: true })}`, {
      scroll: false,
    });
  };

  const isChecked = (value: string) => {
    const params = qs.parse(searchParams.toString());

    return isFilterValueExist(params, FILTER_KEY, value);
  };

  return (
    <>
      <div className="ml-px mt-px">
        <SearchInput placeholder="Cari keahlian" value={keyword} onSearch={handleSearch} />
      </div>
      {options && options.length > 0 ? (
        <>
          <div className="space-y-3 pt-5 pb-2">
            {options.map((option, index) => {
              return (
                <div className="flex items-start gap-3" key={option.value + "-" + index}>
                  {isChecked(option.value)}
                  <Checkbox
                    className="mt-1"
                    id={option.value + "-" + index}
                    onCheckedChange={(checked) => handleFilter(option, checked as boolean)}
                    checked={isChecked(option.value)}
                  />
                  <label htmlFor={option.value + "-" + index}>{option.label}</label>
                </div>
              );
            })}
          </div>
          {/* <SkillFilterDialog path={path} /> */}
        </>
      ) : (
        <div className="text-center font-bold pt-5 pb-2">{!keyword ? "Ketik untuk mencari keahlian" : "Keahlian tidak ditemukan"}</div>
      )}
    </>
  );
}
